import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { getOutbox, getPlayer, type PlayerIdentity, setPlayer } from '@/game/storage'
import { createPlayer, fetchLeaderboard, type Period, renamePlayer } from './client'
import NicknameForm from './NicknameForm'
import { flushOutbox } from './sync'

const PERIODS: { id: Period; label: string }[] = [
  { id: 'day', label: '24 часа' },
  { id: 'week', label: 'Неделя' },
  { id: 'all', label: 'Всё время' },
]

interface Props {
  onClose: () => void
}

export default function Leaderboard({ onClose }: Props) {
  const [period, setPeriod] = useState<Period>('week')
  const [player, setPlayerState] = useState<PlayerIdentity | null>(getPlayer)
  const [renaming, setRenaming] = useState(false)
  const pending = getOutbox().length

  const query = useQuery({
    queryKey: ['leaderboard', period, player?.token ?? null],
    queryFn: async () => {
      // Сначала досылаем свои партии — иначе своего свежего результата в
      // таблице не будет, и это выглядит как баг.
      await flushOutbox()
      return fetchLeaderboard(period, getPlayer()?.token)
    },
    staleTime: 15_000,
    retry: 1,
  })

  const saveNickname = async (nickname: string) => {
    if (player) {
      const saved = await renamePlayer(player.token, nickname)
      const next = { ...player, nickname: saved }
      setPlayer(next)
      setPlayerState(next)
      setRenaming(false)
    } else {
      const created = await createPlayer(nickname)
      setPlayer(created)
      setPlayerState(created)
    }
  }

  const me = query.data?.me
  const meInTop = query.data?.entries.some((e) => e.is_me) ?? false

  return (
    <section className="board" aria-labelledby="board-title">
      <header className="board__header">
        <h2 id="board-title" className="board__title">
          Рекорды
        </h2>
        <button type="button" className="board__close" onClick={onClose}>
          Назад
        </button>
      </header>

      <div className="board__tabs" role="tablist">
        {PERIODS.map((p) => (
          <button
            key={p.id}
            type="button"
            role="tab"
            aria-selected={p.id === period}
            className="board__tab"
            onClick={() => setPeriod(p.id)}
          >
            {p.label}
          </button>
        ))}
      </div>

      <div className="board__body" role="tabpanel">
        {query.isPending && <p className="board__note">Загружаем…</p>}
        {query.isError && (
          <p className="board__note">Таблица недоступна без сети. Ваши партии сохранены.</p>
        )}
        {query.data && query.data.entries.length === 0 && (
          <p className="board__note">За этот период ещё никто не играл — будьте первым.</p>
        )}
        {query.data && query.data.entries.length > 0 && (
          <ol className="board__list">
            {query.data.entries.map((e) => (
              <li key={`${e.rank}-${e.nickname}`} className="board__row" data-me={e.is_me}>
                <span className="board__rank">{e.rank}</span>
                <span className="board__name">{e.nickname}</span>
                <span className="board__score">{e.score}</span>
              </li>
            ))}
            {me && !meInTop && (
              <li className="board__row" data-me="true">
                <span className="board__rank">{me.rank}</span>
                <span className="board__name">{player?.nickname ?? 'Вы'}</span>
                <span className="board__score">{me.score}</span>
              </li>
            )}
          </ol>
        )}
      </div>

      <footer className="board__footer">
        {pending > 0 && player && (
          <p className="board__note">Ещё не отправлено партий: {pending}</p>
        )}
        {!player && (
          <NicknameForm
            label="Придумайте ник, чтобы попасть в таблицу"
            submitLabel="Сохранить"
            onSubmit={saveNickname}
          />
        )}
        {player && !renaming && (
          <p className="board__note">
            Вы играете как «{player.nickname}».{' '}
            <button type="button" className="board__link" onClick={() => setRenaming(true)}>
              Сменить ник
            </button>
          </p>
        )}
        {player && renaming && (
          <NicknameForm
            label="Новый ник"
            submitLabel="Сменить"
            initial={player.nickname}
            onSubmit={saveNickname}
          />
        )}
      </footer>
    </section>
  )
}
